/*
  aro-collection-in-stock-first.js
  v2 replacement for aro-collection-sort-oos-last.js. Two-retrieval split:
    - In-stock subset is fetched with filter.v.availability=1 and paginates
      natively (page 1..N of in-stock only).
    - OOS subset is fetched via the custom.is_fully_oos product-metafield
      storefront filter (maintained by the shopify-tag-fully-oos-products
      n8n workflow, id kGQsZ86VuMuc31nR). One request returns every fully-
      OOS product in the collection. Appended after the last in-stock card
      on the final in-stock page.
    - Address bar stays clean: internal filter params live only in the
      fetch URL, never in history state.

  Scoped to .template-collection only — /search is untouched.
  If the user has explicitly set filter.v.availability (either value) via the
  sidebar, we bow out: no injection, no OOS append. Their filter wins.

  Depends on the theme's FacetFiltersForm (assets/facets.js). Monkey-patches
  renderSectionFromFetch so both the initial load and every filter/sort click
  flow through the same assemble pipeline. Cache in FacetFiltersForm.filterData
  stores the assembled HTML keyed by the user URL, so revisits are fast.
*/
(function () {
  'use strict';

  if (!document.body || !document.body.classList.contains('template-collection')) return;

  var AVAIL_PARAM = 'filter.v.availability';
  var META_PARAM = 'filter.p.m.custom.is_fully_oos';
  var PENDING_ATTR = 'data-aro-in-stock-first-pending';
  var BOUND_FLAG = 'aroPagBound';

  function whenFacetsReady(cb) {
    if (typeof FacetFiltersForm !== 'undefined') return cb();
    var tries = 0;
    var poll = setInterval(function () {
      if (typeof FacetFiltersForm !== 'undefined') {
        clearInterval(poll);
        cb();
      } else if (++tries > 60) {
        // facets.js never loaded (collection with no filters + no sorting).
        // Reveal the server-rendered fallback so the page still works.
        clearInterval(poll);
        reveal();
      }
    }, 50);
  }

  function parseHtml(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function isLastInStockPage(doc) {
    // Paginated mode: <li class="next"> + anchor href indicates a next page.
    var nextLi = doc.querySelector('.pagination li.next');
    if (nextLi) {
      if (nextLi.classList.contains('disabled')) return true;
      return !nextLi.querySelector('a[href]');
    }
    // Load-more / infinite mode always renders the button, even on the last
    // page (the theme's pagination.js is what disables it after clicks). Read
    // the ProductCount label instead: "N-M of TOTAL products" — if M === TOTAL
    // we've reached the end.
    var countLabel = doc.getElementById('ProductCount');
    if (countLabel) {
      // "1-16 of 325 products" — grab the two numbers separated by "of".
      var m = countLabel.textContent.match(/(\d[\d,]*)\s*(?:of|\/)\s*(\d[\d,]*)/i);
      if (m) {
        var currentEnd = parseInt(m[1].replace(/,/g, ''), 10);
        var total = parseInt(m[2].replace(/,/g, ''), 10);
        if (!isNaN(currentEnd) && !isNaN(total)) return currentEnd >= total;
      }
    }
    // Fallback: no pagination markup and no count → single-page collection.
    return true;
  }

  function pickCardLis(grid) {
    if (!grid) return [];
    return Array.from(grid.children).filter(function (li) {
      if (li.tagName !== 'LI') return false;
      if (li.classList.contains('loading-overlay')) return false;
      if (li.classList.contains('promotion-column')) return false;
      return true;
    });
  }

  function fetchOosCards(sectionId) {
    // Direct product-metafield storefront filter — every fully-OOS product carries
    // custom.is_fully_oos=true (maintained by the shopify-tag-fully-oos-products
    // n8n workflow). One request returns up to the theme's per_page, which for
    // typical Aromantic category collections covers the full OOS set.
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&' + META_PARAM + '=true';
    return fetch(url).then(function (r) { return r.text(); }).then(function (text) {
      var doc = parseHtml(text);
      var grid = doc.getElementById('product-grid');
      if (!grid || grid.classList.contains('collection-empty')) return [];
      return pickCardLis(grid);
    }).catch(function () { return []; });
  }

  function assembleFullHtml(inStockText, oosLis) {
    var doc = parseHtml(inStockText);
    if (!isLastInStockPage(doc)) return inStockText;
    var grid = doc.getElementById('product-grid');
    if (!grid || oosLis.length === 0) return inStockText;
    // If the in-stock page is empty (0 in-stock in the whole collection), the
    // theme renders <div class="collection-empty">. Skip the append — there's
    // no <ul> to append to; the empty state is the right UX.
    if (grid.classList.contains('collection-empty')) return inStockText;
    var loadingOverlay = grid.querySelector(':scope > .loading-overlay');
    oosLis.forEach(function (li) {
      if (loadingOverlay) grid.insertBefore(li, loadingOverlay);
      else grid.appendChild(li);
    });
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  }

  function reveal() {
    if (document.body.hasAttribute(PENDING_ATTR)) {
      document.body.removeAttribute(PENDING_ATTR);
    }
  }

  function bindPaginationLinks() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var links = container.querySelectorAll('.pagination a[href]');
    links.forEach(function (a) {
      if (a.dataset[BOUND_FLAG] === '1') return;
      a.dataset[BOUND_FLAG] = '1';
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        var href = a.getAttribute('href') || '';
        if (!href || href.indexOf('#') === 0) return;
        var qm = href.indexOf('?');
        var newParams = qm !== -1 ? href.slice(qm + 1) : '';
        e.preventDefault();
        FacetFiltersForm.renderPage(newParams, e);
      });
    });
  }

  function install() {
    var origRenderFromFetch = FacetFiltersForm.renderSectionFromFetch;

    FacetFiltersForm.renderSectionFromFetch = function (url, event) {
      var qm = url.indexOf('?');
      if (qm === -1) return origRenderFromFetch.call(this, url, event);
      var path = url.slice(0, qm);
      var params = new URLSearchParams(url.slice(qm + 1));
      var sectionId = params.get('section_id');

      // User has an explicit availability or OOS metafield filter set — respect
      // it. No split, no OOS append (they've filtered to a specific subset).
      if (params.has(AVAIL_PARAM) || params.has(META_PARAM)) {
        return origRenderFromFetch.call(this, url, event);
      }

      params.set(AVAIL_PARAM, '1');
      var inStockUrl = path + '?' + params.toString();

      Promise.all([
        fetch(inStockUrl).then(function (r) { return r.text(); }),
        fetchOosCards(sectionId),
      ]).then(function (results) {
        var assembled = assembleFullHtml(results[0], results[1]);

        FacetFiltersForm.filterData = FacetFiltersForm.filterData.concat([{ html: assembled, url: url }]);
        FacetFiltersForm.renderFilters(assembled, event);
        FacetFiltersForm.renderProductGridContainer(assembled);
        FacetFiltersForm.renderProductCount(assembled);
        if (typeof FacetsToggle !== 'undefined') new FacetsToggle();
        if (typeof SidebarToggle !== 'undefined') new SidebarToggle();

        reveal();
        bindPaginationLinks();
      }).catch(function () {
        // If either fetch fails, fall through to the theme's original path so
        // the user always sees SOMETHING rather than an empty grid.
        origRenderFromFetch.call(FacetFiltersForm, url, event);
        reveal();
      });
    };

    // Cache path (renderSectionFromCache) also needs post-swap wiring for
    // pagination binds when the theme serves from cache.
    var container = document.getElementById('ProductGridContainer');
    if (container && 'MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        reveal();
        bindPaginationLinks();
      });
      observer.observe(container, { childList: true });
    }

    // Trigger initial swap: the server-rendered fallback grid is currently
    // hidden by the section's inline style. renderPage flows through our patch.
    FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { whenFacetsReady(install); });
  } else {
    whenFacetsReady(install);
  }
})();
